/**
 * RESPONSIBILITY: draw the Give page — how to give.
 *
 * The House of Honor appeal makes its case on a page of its own — see HouseOfHonor.tsx.
 */
import { Html } from '@framework/react';
import type { GiveSection } from '@app/sections';
import { PageHead } from '../commons/PageHead';
import { Crumbs } from '../commons/Crumbs';
import { Page, hrefFor } from '../commons/pages';
import { useWords } from '../commons/words';
import { Enlarge } from '../commons/Enlarge';
import { hasImage, idOf } from '@framework/core';

export function Give({ c }: { c: GiveSection }) {
  const w = useWords();
  const ways = c.ways ?? [];

  return (
    <>
      <Crumbs trail={[{ label: w.giveLabel }]} />
      <PageHead eyebrow={c.eyebrow} heading={c.heading} intro={c.intro} />

      {ways.length > 0 && (
        <section className="band">
          <div className="wrap">
            <div className="band-head"><h2>{c.waysHeading}</h2></div>
            {/* Masonry, not a grid: each card is as tall as what it says, so the one with a
                QR code does not stretch the three beside it (James, 2026-10-17). */}
            <div className="give-ways">
              {ways.map((w) => (
                <div className="give-way" key={idOf(w)}>
                  <h3>{w.title}</h3>
                  {/* The rendered markdown, with its :highlight and :::address. A row saved
                      before the field was markdown has no rendering yet: its text is shown
                      line for line, as typed, until the next save renders it (R68). */}
                  {w.blurbHtml
                    ? <Html className="md-body give-notes">{w.blurbHtml}</Html>
                    : w.blurb && <p className="give-notes give-plain">{w.blurb}</p>}
                  {hasImage(w.picture) && <Enlarge value={w.picture} className="give-pic" />}
                  {w.ctaLabel && (
                    <a className="btn btn-ghost give-btn" href={w.ctaHref || '#'}>{w.ctaLabel}</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="band band-sand band-centre">
        <div className="wrap">
          <h2>{c.askHeading}</h2>
          {c.askBody && <p className="lede">{c.askBody}</p>}
          <div className="btns btns-centre">
            <a className="btn btn-ghost" href={c.askCtaHref || hrefFor(Page.Contact)}>
              {c.askCtaLabel}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
